/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';
import { useSearchParams } from 'next/navigation';
import { showError } from '@utils';
import { selectLoggedInUserState } from '@store';

import { QUERY_KEYS } from 'constants/queryKeys';

import { communicationServices } from '@services';

import { useAssignees, useSelectorRef } from 'hooks/common';
import { useMailBoxesOperations } from 'hooks/mailBoxes';
import {
  getThreadPayload,
  handleEmail,
  handleHistory,
  handleThreadData,
} from './utils';
import { DEFAULT_MAILBOX } from './constants';

export const useEmailOperations = () => {
  const searchParams = useSearchParams();
  const currentUser = useSelectorRef(selectLoggedInUserState);
  const {
    assignees,
    isFetching: isAssigneesFetching,
    isLoading: isAssigneesLoading,
  } = useAssignees();
  const { mailBoxes, isFetched: mailBoxesIsFetched } = useMailBoxesOperations();
  const {
    back,
    query: { threadId },
  } = useRouter();
  const mailboxId = searchParams.get('mailboxId');

  const threadResponse = useQuery(
    [QUERY_KEYS.GET_MAILBOX_THREAD, { mailboxId, threadId }],
    () => {
      const params = {
        page: 1,
        pageSize: 300,
        orderBy: 'messageDate',
        order: 'asc',
      };

      const payload = getThreadPayload({ threadId, mailboxId });
      return communicationServices.getEmails({
        params,
        payload,
      });
    },
    {
      enabled: !!(mailboxId && threadId && mailBoxesIsFetched),
    }
  );
  const lastEmailId =
    threadResponse?.data?.data?.length >= 1
      ? threadResponse.data.data[threadResponse.data.data.length - 1].id
      : undefined;

  const mailResponse = useQuery(
    [QUERY_KEYS.GET_MAIL, lastEmailId],
    () => communicationServices.getEmailById(lastEmailId),
    { enabled: !!lastEmailId }
  );

  const mailEntitiesResponse = useQuery(
    [QUERY_KEYS.GET_MAIL_ENTITIES_KEY, lastEmailId],
    () => communicationServices.getEmailEntities(lastEmailId),
    { enabled: !!lastEmailId }
  );

  const historyResponse = useQuery(
    [QUERY_KEYS.GET_MAIL_HISTORY, lastEmailId],
    () => communicationServices.getEmailHistoryById(lastEmailId),
    { enabled: !!lastEmailId }
  );

  useEffect(() => {
    if (threadResponse?.error) {
      showError(threadResponse?.error);
    }
  }, [threadResponse?.error]);

  useEffect(() => {
    if (mailResponse?.error) {
      showError(mailResponse?.error);
    }
  }, [mailResponse?.error]);

  useEffect(() => {
    if (historyResponse?.error) {
      showError(historyResponse?.error);
    }
  }, [historyResponse?.error]);

  useEffect(() => {
    if (mailEntitiesResponse?.error) {
      showError(mailEntitiesResponse?.error);
    }
  }, [mailEntitiesResponse?.error]);

  const refetch = async () => {
    await threadResponse?.refetch();
    await mailResponse?.refetch();
    await mailEntitiesResponse?.refetch();
    await historyResponse?.refetch();
  };

  const refetchMailEntities = async () => {
    await mailEntitiesResponse?.refetch();

    await historyResponse?.refetch();
  };

  const thread = handleThreadData(threadResponse?.data?.data || []);
  const assigneesMap = assignees.reduce((acc, item) => {
    acc[item.email] = item;
    return acc;
  }, {});
  const history = handleHistory(historyResponse?.data?.data || []);
  const email = handleEmail(mailResponse?.data?.data, mailBoxes);
  const isFetching =
    threadResponse?.isFetching ||
    isAssigneesFetching ||
    mailResponse?.isFetching ||
    historyResponse?.isFetching ||
    mailEntitiesResponse?.isFetching;
  const agent = assignees?.find((item) => item.email === currentUser?.email);

  // https://linear.app/instacar/issue/INS-4845/instafleet-unified-view-log-comms-to-bookingssubscriptions-history#comment-72c6388e

  /**
   * When determining which email to use:
   *
   * - If the email is inbound (from customer → us):
   *   Use the sender's email (i.e. the customer), NOT any of the recipients/CCs.
   *
   * - If the email is outbound (from us → customer):
   *   Use the main recipient's email (typically only one).
   *
   * Note:
   * Agents are expected to initiate communication through the Booking/Sub flow,
   * where communications are automatically recorded in the deal history.
   * This flow is already implemented and should be preferred over manual email handling.
   */
  const allParticipants = [
    email?.from,
    ...(email?.to || []),
    ...(email?.cc || []),
  ].filter(Boolean);

  const isInternal = (participant) =>
    mailBoxes.some(
      (mailBox) =>
        mailBox.address === participant?.emailAddress ||
        participant.emailAddress === DEFAULT_MAILBOX
    );

  const clientOfEmail = allParticipants.find((p) => !isInternal(p)) || null;
  const connectedEntities = mailEntitiesResponse?.data?.data || [];
  const hasConnectedEntity = connectedEntities.length > 0;
  // shows the latest connected entity, as there should be only one connected entity in the current UI/UX design
  // TODO: If in the future we support multiple connected entities, we should change this to return all connected entities and update the UI accordingly
  const connectedEntity =
    connectedEntities[connectedEntities.length - 1] || null;
  return {
    loading:
      threadResponse?.isLoading ||
      isAssigneesLoading ||
      mailResponse?.isLoading ||
      historyResponse?.isLoading ||
      mailEntitiesResponse?.isLoading,
    isFetching,
    refetchMailEntities,
    connectedEntities,
    emailId: email?.id,
    email,
    assignees,
    agent,
    clientOfEmail,
    hasConnectedEntity,
    connectedEntity,
    history,
    thread,
    threadId,
    assigneesMap,
    currentUser,
    refetch,
    goBack: back,
  };
};
